/**
 * bodyIsolation 'shadow-dom' — 본문 shadow root 에 거는 stylesheet.
 *
 * shadow 안에는 앱 전역 reset(reboot·antd·Tailwind preflight)이 닿지 않으므로, `.htmlMailBody :where(…)`
 * 의 UA 되돌리기(revert) 규칙은 넣지 않는다(UA 기본값이 곧 그 결과). 여기에는 본문에 "실제 값"을 주는
 * 기능 규칙만 둔다. CSS Modules 는 selector 를 hash 하므로 plain CSS 문자열로 두고, constructable sheet
 * 를 singleton 으로 만들어 모든 본문이 같은 object 를 adoptedStyleSheets 로 공유한다(메일 N 통에 parse 1회).
 *
 * 값은 아래 원본과 같아야 한다 — bodyShadowSheet.unit.test.ts 가 drift 를 잡는다.
 *   - EmailContainer/style.module.scss  `.hidden`·`.nf-removeZoomBtn`·`.nf-zoom-container:hover`
 *   - EmailView/style.module.scss       `.htmlMailBody :where(a)`(색 = scss/main `$text`)·`:where(pre)`
 *   - TextFinder/style.module.scss      `::highlight(findable-text|focused-findable-text)`
 *
 * `:host { overflow-wrap }`: 번들은 `.htmlMailBody :where(*)` 로 host 의 자손에만 break-word 를 주므로
 * shadow 안 본문은 host 에서 상속할 값이 없다. `:host` 규칙은 바깥 문서 규칙보다 약해, host 쪽에 값이
 * 있으면(desktop inner `.htmlMailBody { overflow-wrap: anywhere }`) 그 값이 이긴다.
 */
export const BODY_SHADOW_CSS = `
:host { overflow-wrap: break-word; }
.hidden { display: none !important; }
.nf-removeZoomBtn {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 32px;
  height: 32px;
  background-color: #fff;
  border: solid 1px #000;
  border-radius: 4px;
  opacity: 0.5;
}
.nf-zoom-container:hover > .nf-removeZoomBtn { display: block; }
a { color: #595c97; text-decoration: underline; }
a:visited, a:hover, a:active, a:focus { text-decoration: underline; }
pre { white-space: pre-wrap; }
::highlight(findable-text) { background-color: #ff5; border-radius: 2px; }
::highlight(focused-findable-text) { background-color: #f19b4b; border-radius: 2px; }
`

let bodySheet: CSSStyleSheet | null = null
// 다크 링크·본문 색 — light DOM 경로의 `#darkmode-info` 와 같은 선언을 shadow 쪽에 미러한다.
let darkSheet: CSSStyleSheet | null = null
// 위 본문 기능 규칙의 다크 변환판 — 문서 <style> 은 `_checkStyleTagCss` 가 `data-nine-style` 로 바꾸지만
// adoptedStyleSheets 는 <style> 이 아니라 그 walk 에 잡히지 않는다. 같은 변환 결과를 여기에 둔다.
let convertedSheet: CSSStyleSheet | null = null

function ensureSheets(): [CSSStyleSheet, CSSStyleSheet, CSSStyleSheet] {
  if (bodySheet == null) {
    bodySheet = new CSSStyleSheet()
    bodySheet.replaceSync(BODY_SHADOW_CSS)
  }
  darkSheet ??= new CSSStyleSheet()
  convertedSheet ??= new CSSStyleSheet()
  return [bodySheet, darkSheet, convertedSheet]
}

function canUseSheets(): boolean {
  return (
    typeof CSSStyleSheet !== 'undefined' &&
    typeof CSSStyleSheet.prototype.replaceSync === 'function'
  )
}

/** 본문 shadow root 의 adoptedStyleSheets — [본문 기능 규칙, 다크 색, 기능 규칙의 다크 변환]. */
export function getBodyShadowSheets(): CSSStyleSheet[] {
  return ensureSheets()
}

/** 다크 색 선언 갱신(light 복귀는 빈 문자열). shadow 를 쓰지 않는 환경에서도 호출해도 무해하다. */
export function setBodyShadowDarkCss(css: string): void {
  if (!canUseSheets()) return
  ensureSheets()[1].replaceSync(css)
}

/** 본문 기능 규칙 sheet(다크 변환 입력). 미지원 환경이면 null. */
export function getBodyShadowSheet(): CSSStyleSheet | null {
  return canUseSheets() ? ensureSheets()[0] : null
}

/** 기능 규칙의 다크 변환 결과 갱신(light 복귀는 빈 문자열). */
export function setBodyShadowConvertedCss(css: string): void {
  if (!canUseSheets()) return
  ensureSheets()[2].replaceSync(css)
}
